import {
  GovernmentFundedDetailValue,
  governmentFundedProgramNames,
  RDGovernmentFunded,
} from '@cibo/core'
import { ResourceDetailFeatureTaskEditorProps } from '@cibo/ui'
import { Checkbox, FormControlLabel, FormGroup, Stack, TextField, Typography } from '@mui/material'
import { useDebounceCallback } from '@react-hook/debounce'
import { equals } from 'ramda'
import { ChangeEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useProgramEngagementContext } from '../../../context'
import { useProgramEngagementDetails, useUpdateProgramEngagementDetails } from '../../../queries'

type GovernmentFundedDetail = {
  traitId: 'governmentFunded'
  year: number
  value: GovernmentFundedDetailValue
  input: never
  result: never
}

const detailIsValid = (value: Partial<GovernmentFundedDetailValue>) => {
  return (
    value &&
    (value.isFunded === false ||
      (value.isFunded === true &&
        !!value.data &&
        (!!value.data.programs?.length || value.data.otherProgram !== '')))
  )
}

export const RDGovernmentFundedEditor = ({
  detailRequirements,
}: ResourceDetailFeatureTaskEditorProps<RDGovernmentFunded>) => {
  const { t } = useTranslation('@cibo/programs/GovernmentFundedEditor')

  const { engagementId } = useProgramEngagementContext()
  const details = useProgramEngagementDetails<any>({
    resourceId: engagementId,
    detailRequirements: [
      {
        traitId: 'governmentFunded',
        year: detailRequirements[0]?.year,
        dataType: 'workflow',
      },
    ],
  })
  const updateEngagementDetails = useUpdateProgramEngagementDetails<GovernmentFundedDetail>({
    engagementId: engagementId as string,
  })

  const [localValue, setLocalValue] = useState<Partial<GovernmentFundedDetailValue>>({
    isFunded: undefined,
    data: {
      programs: [],
      otherProgram: undefined,
    },
  })

  // This effect sets the initial value from persisted value
  useEffect(() => {
    if (details && !!details.data?.length) {
      const detail = details.data[0]
      setLocalValue(detail.value)
    }
  }, [details.isFetched])

  // this effect synchronizes the local and persisted values
  useEffect(() => {
    if (equals(localValue, details.data?.[0]?.value)) {
      return
    } else {
      persistDetailValue(localValue)
    }
  }, [localValue])

  const persistDetailValue = useDebounceCallback(
    (localValue: Partial<GovernmentFundedDetailValue>) => {
      const isValid = detailIsValid(localValue)

      engagementId &&
        isValid &&
        updateEngagementDetails
          .mutateAsync({
            resourceId: engagementId,
            details: [
              {
                traitId: 'governmentFunded',
                year: detailRequirements[0]?.year as number,
                value: localValue as GovernmentFundedDetailValue,
              } as GovernmentFundedDetail,
            ],
          })
          .catch(() => {
            setLocalValue(details.data?.[0]?.value)
          })
    },
    300
  )

  const onClickNo = (_: any, checked: boolean) => {
    setLocalValue({
      isFunded: !checked,
      data: {
        programs: [],
        otherProgram: undefined,
      },
    })
  }

  const onClickProgram = (event: any, checked: boolean) => {
    const programs = localValue.data?.programs || []
    const newPrograms =
      programs.includes(event.target.value) && !checked
        ? programs.filter(program => program !== event.target.value)
        : [...programs, event.target.value]

    setLocalValue({
      isFunded: true,
      data: {
        programs: newPrograms,
        otherProgram: localValue.data?.otherProgram,
      },
    })
  }

  return (
    <Stack>
      <Typography>{t('question')}</Typography>
      <FormGroup>
        <FormControlLabel
          onChange={onClickNo}
          control={<Checkbox />}
          label={t('no')}
          value="no"
          checked={localValue.isFunded === false}
        />
        {governmentFundedProgramNames.map(program => (
          <FormControlLabel
            key={program}
            onChange={onClickProgram}
            control={<Checkbox />}
            label={t(`governmentProgram_${program}`)}
            value={program}
            checked={localValue.data?.programs?.includes(program) || false}
          />
        ))}
        <FormControlLabel
          control={<Checkbox />}
          checked={localValue.data?.otherProgram !== undefined}
          onChange={(event, checked) =>
            setLocalValue(prev => ({
              isFunded: checked ? true : prev.isFunded,
              data: {
                ...prev.data,
                otherProgram: checked ? prev.data?.otherProgram || '' : undefined,
              },
            }))
          }
          label={
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography>{t('other')}</Typography>
              <TextField
                value={localValue.data?.otherProgram || ''}
                placeholder={t('enterProgramName')}
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
                  setLocalValue(prev => ({
                    isFunded: true,
                    data: {
                      ...prev.data,
                      otherProgram: event.target.value,
                    },
                  }))
                }}
              />
            </Stack>
          }
          value="other"
        />
      </FormGroup>
    </Stack>
  )
}
